import { Box, CircularProgress } from '@mui/material'
import DollarIcon from '@mui/icons-material/AttachMoney'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import formatCurrency from '@/utils/formatCurrency'
import ItemCard from '@/components/ItemCard'
import TEarningSummary from '@/concepts/Earnings/types/EarningsSummary'
import { useListContext } from 'react-admin'
import InfoTooltip from '@/components/InfoTooltip'
import { isVendor } from '@/authProvider'

type Props = {
  summary: TEarningSummary
}

const styles = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    mb: 3
  }
}

const EarningsSummary = ({ summary }: Props) => {
  const { meta, isLoading } = useListContext()

  const partnerSummary = {
    netEarnings: isVendor() ? meta?.earnings : summary.netEarnings,
    itemsSold: isVendor() ? meta?.net : summary.sales,
    grossEarnings: summary.grossEarnings,
    chargebacks: meta?.chargebacks || 0,
    chargeback_items: meta?.chargeback_items
  }

  const netEarningsLabel = 'Net Earnings'
  const netEarningsTooltipText = isVendor()
    ? 'These are your estimated net earnings after deductions.'
    : 'Gross Earnings - Paid Spend = Net Earnings'

  if (isLoading || meta === undefined) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    )
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: '50px',
        marginTop: '20px',
        p: '1rem',
        gap: '10px'
      }}
    >
      <ItemCard
        icon={DollarIcon}
        title={
          <InfoTooltip
            label={netEarningsLabel}
            tooltipText={netEarningsTooltipText}
          />
        }
        data={formatCurrency(partnerSummary.netEarnings || 0)}
        testId="net-earnings"
      />
      {!isVendor() && (
        <ItemCard
          icon={DollarIcon}
          title="Gross Earnings"
          data={formatCurrency(partnerSummary.grossEarnings || 0)}
          testId="gross-earnings"
        />
      )}
      <ItemCard
        icon={ShoppingCartIcon}
        title={
          isVendor() ? (
            <InfoTooltip
              label="Net Items Sold"
              tooltipText="Net Items Sold = Total Sold - Number of Fully Refunded Items. Note: Partial refunds are not included in this calculation."
            />
          ) : (
            'Items Sold'
          )
        }
        data={partnerSummary.itemsSold || 0}
        testId="items-sold"
      />

      {isVendor() && (
        <>
          <ItemCard
            icon={DollarIcon}
            title="Chargebacks"
            data={formatCurrency(partnerSummary.chargebacks || 0)}
            testId="chargebacks"
          />

          <ItemCard
            icon={DollarIcon}
            title="Net Items Chargeback "
            data={partnerSummary.chargeback_items || 0}
            testId="chargeback-items"
          />
        </>
      )}
    </Box>
  )
}

export default EarningsSummary
